<script>
import HeroRecipeMinimal from '@components/hero/hero-recipe-minimal.vue';
import MacroList from '@components/macros/macro-list.vue';
import {
  recipeComputed,
  adminPanelComputed,
  platelistComputed,
  platelistMethods,
  adminPanelMethods,
  authComputed,
  recipeMethods,
} from '@src/state/helpers';

export default {
  components: {
    HeroRecipeMinimal,
    MacroList,
  },
  props: {},
  data() {
    return {
      isDeleting: false,
      isSaving: false,
      showIngredients: false,
      showInstructions: false,
    };
  },
  computed: {
    ...authComputed,
    ...adminPanelComputed,
    ...platelistComputed,
    ...recipeComputed,
    hasIngredients() {
      return (
        this.selectedRecipe &&
        this.selectedRecipe.ingredients &&
        this.selectedRecipe.ingredients.length
      );
    },
    isRecipeInPlan() {
      return (
        this.panelContent &&
        this.panelContent.data &&
        this.panelContent.data.isRecipeInPlan
      );
    },
    isPlatelistLocked() {
      return (
        this.selectedPlatelist &&
        this.selectedPlatelist.editabilityStatus &&
        this.selectedPlatelist.editabilityStatus.locked
      );
    },
  },
  mounted() {},
  methods: {
    ...adminPanelMethods,
    ...platelistMethods,
    ...recipeMethods,
    clearSelections() {
      this.setSelectedRecipe(null);
      this.setSelectedPlanDay(null);
      this.setSelectedMealtime(null);
    },
    refreshPlanGrid() {
      this.setHideAdminPanel();
      this.getAdminPlanGrid({
        platelistId: this.selectedPlanDay.platelistId,
        weekOffset: this.selectedWeekOffset,
      });
      this.clearSelections();
      this.isDeleting = false;
    },
    async handleLeftovers() {
      this.isSaving = true;
      const action =
        this.selectedRecipe.hasLeftovers || this.selectedRecipe.useLeftovers
          ? 'unFlagForLeftovers'
          : 'flagForLeftovers';
      await this.updateLeftoverDish({
        action: action,
        platelistDayId: this.selectedPlanDay.id,
        platelistMealId: this.selectedRecipe.platelistMealId,
        mealTimeId: this.selectedMealtime.id,
        recipeId: this.selectedRecipe.id,
      }).catch((e) => console.log(e));
      this.refreshPlanGrid();
      this.isSaving = false;
    },
    async removeMeal() {
      this.isDeleting = true;
      await this.removeMealFromPlatelist(this.selectedRecipe.platelistMealId);
      this.refreshPlanGrid();
      this.isDeleting = false;
    },
  },
  watch: {
    isAdminPanelActive(newVal, oldVal) {
      if (!newVal && oldVal) {
        this.showIngredients = false;
        this.showInstructions = false;
      }
    },
  },
};
</script>

<template>
  <div v-if="selectedRecipe" class="recipe-preview">
    <HeroRecipeMinimal v-if="selectedRecipe" :recipe="selectedRecipe" />

    <div class="recipe-preview-content">
      <label>Description</label>
      <p class="recipe-description" v-if="selectedRecipe.description">
        {{ selectedRecipe.description }}
      </p>

      <div class="row">
        <div class="col">
          <label>Ingredients</label>
          <p v-if="hasIngredients">
            <span
              class="ingredient"
              v-for="(ingredient, i) in selectedRecipe.ingredients"
              :key="i"
            >
              {{ ingredient.name
              }}<span v-if="i !== selectedRecipe.ingredients.length - 1"
                >,</span
              >
            </span>
          </p>
        </div>
        <div class="col">
          <MacroList
            v-if="selectedRecipe"
            :recipe="selectedRecipe"
            :showCalories="true"
          />
        </div>
      </div>

      <ul class="list-recipe-details">
        <li @click="showIngredients = !showIngredients">
          <h4>Ingredients</h4>
          <img
            :class="['icon-chevron-down', { up: showIngredients }]"
            src="@assets/images/icon-chevron-down.svg"
          />
        </li>
        <div :class="['list-recipe-detail-item', { visible: showIngredients }]">
          <span
            class="ingredient-item"
            v-for="(ingredient, i) in selectedRecipe.ingredients"
            :key="i"
          >
            <p v-if="ingredient"
              >{{ ingredient.count }}
              <span v-if="ingredient.metricInfo">{{
                ingredient.metricInfo.name
              }}</span>
              {{ ingredient.name }}</p
            >
          </span>
        </div>
        <li @click="showInstructions = !showInstructions">
          <h4>Instructions</h4>
          <img
            :class="['icon-chevron-down', { up: showInstructions }]"
            src="@assets/images/icon-chevron-down.svg"
          />
        </li>
        <div
          :class="['list-recipe-detail-item', { visible: showInstructions }]"
        >
          <span
            class="instruction-item"
            v-for="(instruction, i) in selectedRecipe.instructions"
            :key="i"
          >
            <label>Step {{ instruction.step }}</label>
            <p>{{ instruction.description }}</p>
          </span>
        </div>
      </ul>
    </div>
    <div class="panel-footer" v-if="isRecipeInPlan && !isPlatelistLocked">
      <BaseButton
        type="button"
        class="btn-primary btn-clear"
        @click="removeMeal"
      >
        <BaseIcon v-if="isDeleting" name="sync" spin />
        <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-trash'" v-bind:svg-inline="''" v-if="!isDeleting" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
        Remove from {{ selectedPlanDay ? selectedPlanDay.headerValue : '' }}
      </BaseButton>
      <span class="divider-vertical" v-if="!selectedRecipe.useLeftovers"
        >&nbsp;</span
      >
      <BaseButton
        v-if="!selectedRecipe.useLeftovers"
        type="button"
        class="btn-primary btn-clear"
        @click="handleLeftovers"
      >
        <BaseIcon v-if="isSaving" name="sync" spin />
        <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-make-leftovers'" v-bind:svg-inline="''" v-if="!isSaving" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M6.786 14.962c3.623 0 6.624-3.001 6.624-6.631 0-2.146-1.073-4.108-2.707-5.305-.307-.239-.683-.17-.861.103-.192.287-.103.615.178.834a5.426 5.426 0 012.214 4.368 5.424 5.424 0 01-5.448 5.448 5.422 5.422 0 01-5.441-5.448 5.418 5.418 0 014.163-5.298v.937c0 .465.335.594.704.335L8.317 2.82c.301-.218.308-.547 0-.765L6.22.572c-.376-.266-.711-.136-.711.335v.923C2.479 2.432.155 5.146.155 8.331c0 3.63 3.008 6.63 6.631 6.63zM3.457 5.993c-.027.294-.055.588-.055.868 0 2.215 1.456 3.91 3.343 3.91.827 0 1.593-.355 1.928-.957.294.39.437.848.683 1.566.021.068.096.096.15.061l.226-.109c.164-.068.226-.164.226-.335 0-.478-.615-1.196-.93-1.483C7.702 8.29 5.658 9 5.166 7.654a.157.157 0 01-.02-.061c0-.055.04-.096.082-.096.047 0 .075.027.109.062 1.012 1.011 2.42.164 3.698 1.353.103.082.233.055.24-.068.013-.103.02-.219.02-.349 0-1.483-1.046-2.112-2.543-2.112-.472 0-.95.061-1.395.061-.628 0-1.189-.109-1.572-.526-.116-.116-.307-.096-.328.075z" fill="#fff"/></svg>
        <span v-if="selectedRecipe.hasLeftovers">Discard Leftovers</span>
        <span v-else>Make Leftovers</span>
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.recipe-description {
  margin-bottom: 4.1rem;
}

.ingredient {
  text-transform: capitalize;
}

::v-deep .list-macros {
  margin-bottom: 0;
}

.list-recipe-details {
  border-top: 1px solid $color-gray;
  margin-top: 3.9rem;

  li {
    border-bottom: 1px solid $color-gray;
    display: flex;
    justify-content: space-between;
    padding: 1.8rem 2.6rem 1.8rem 0;

    &:hover {
      cursor: pointer;
    }

    .icon-chevron-down {
      margin-left: 1rem;

      &.up {
        transform: rotate(180deg);
      }
    }
  }

  .list-recipe-detail-item {
    height: 0;
    overflow: hidden;
    transition: height $duration-animation-base ease-in-out;

    &.visible {
      height: 100%;
    }

    .ingredient-item,
    .instruction-item {
      border-bottom: 1px solid $color-gray;
      display: block;
      margin-bottom: 2.1rem;
      padding-bottom: 2.1rem;
      width: 100%;
    }

    .instruction-item:first-child {
      padding-top: 3.1rem;
    }

    .ingredient-item {
      margin-bottom: 1rem;
      padding-bottom: 1rem;

      &:first-child {
        padding-top: 1.1rem;
      }

      p {
        margin-bottom: 0;
        text-transform: capitalize;
      }
    }
  }
}

.panel-footer {
  background-color: $color-header-bg;
  border-radius: $size-input-border-radius;
  bottom: 1rem;
  display: flex;
  justify-content: space-around;
  padding: 1rem 2rem;
  position: fixed;
  width: 94%;
  z-index: 9999;

  &.form-error {
    background-color: $color-error-dark;
  }

  .divider-vertical {
    border-left: 1px solid white;
    height: 4rem;
  }

  .btn-primary {
    &:active {
      background: rgba($color-white-wo-rgb, 0.1);
      color: white;
    }

    &:focus {
      background: transparent;
      color: white;
    }

    &:not(:disabled):not(.disabled) {
      &:active {
        background: rgba($color-white-wo-rgb, 0.1);
        color: white;
      }

      &:focus {
        background: transparent;
        color: white;
      }
    }

    .fa-sync,
    .icon-trash,
    .icon-make-leftovers {
      margin-right: 1rem;
    }

    .icon-trash {
      path {
        fill: white;
      }
    }

    .icon-make-leftovers {
      height: 2rem;
      width: 2rem;
    }
  }

  .btn-clear {
    color: white;
  }
}
</style>
